// src/modules/units/tyres/views/import/tyreimport.js
//
// Orchestrator for the tyre import wizard: File → Map → Review → Import.
//
// It owns the wizard's state and wiring; everything that can be pure is not here:
//   reading the file        shared/tyre-import-file.js
//   fields + header guess   shared/tyre-import-schema.js
//   coercion + validation   shared/tyre-import-values.js, tyre-import-validate.js
//   batching + results      shared/tyre-import-run.js
//   markup                  tyreimport.view.js
//   the writes              tyres.api.js saveTyres() — which emits CHANGED, one
//                           per batch; this view never emits
//
// Redraw policy: each step has named slots and a change repaints only the slot it
// affects, so a mapping dropdown is never rebuilt under the user's focus.
//
// Leaving the screen mid-import (header Back) disposes the view; the runner polls
// that between batches, so it finishes the batch in flight and stops cleanly.

import { fetchAllTyres, fetchTyre, saveTyres } from '../tyres.api.js';
import { fetchYards } from '../yards.api.js';
import { fetchSuppliers } from '../suppliers.api.js';
import { fetchTyreCatalogue, createTyreCatalogueEntry } from '../tyre-catalogue.api.js';
import { getUserData } from '../../../../auth/views/login/auth.api.js';
import { getSelectedYard } from '../../shared/tyre-scope.js';
import { todayInputValue } from '../../shared/tyre-dates.js';
import { csvCell, downloadCsv, downloadBlob } from '../../shared/tyre-csv.js';
import { writeXlsx, XLSX_MIME } from '../../shared/tyre-xlsx-write.js';
import { buildTemplateWorkbook, templateFileName } from './tyreimport-template.js';
import { readSpreadsheet } from '../../shared/tyre-import-file.js';
import {
  autoMap, detectHeaderRow, headersFor, headerSignature, mappingProblems
} from '../../shared/tyre-import-schema.js';
import {
  buildImportRows, rowsToRun, tyreIdKey, EXISTING_MODE, IMPORT_ACTION
} from '../../shared/tyre-import-validate.js';
import { runImport, runCatalogue, resultCounts, IMPORT_RESULT } from '../../shared/tyre-import-run.js';
import { loadImportMapping, saveImportMapping } from '../../shared/tyre-import-prefs.js';
import {
  importShellHTML, stepsHTML,
  fileStepHTML, previewHTML, fileFooterHTML,
  mapStepHTML, mapProblemsHTML, mapFooterHTML,
  reviewStepHTML, reviewFooterHTML, REVIEW_FILTERS,
  runStepHTML, runFooterHTML, RUN_FILTERS,
  PAGE_SIZE
} from './tyreimport.view.js';

function el(html) {
  const t = document.createElement('template');
  t.innerHTML = html.trim();
  return t.content.firstElementChild;
}

const pageCount = (n) => Math.max(1, Math.ceil(n / PAGE_SIZE));
const clampPage = (p, n) => Math.min(Math.max(0, p), pageCount(n) - 1);
const stamp = () => new Date().toISOString().slice(0, 16).replace(/[-:T]/g, '');

/**
 * @param {HTMLElement} viewEl controller view slot (emptied first)
 * @param {object} ctx
 * @param {() => void} [ctx.onClose]                  Cancel on step 1
 * @param {(yard:object|null) => void} [ctx.onOpenRegister]  "View tyres" after an import
 * @returns {() => void} teardown
 */
export function renderTyreImport(viewEl, ctx = {}) {
  let disposed = false;

  const state = {
    step: 'file',

    yards: [], yardsStatus: 'loading', yardsError: '',
    yardId: getSelectedYard()?.Id ?? '',
    suppliers: [], suppliersStatus: 'loading',
    templateBusy: false,

    fileName: '', reading: false,
    sheets: [], sheetIndex: 0, headerRow: 0,

    headers: [], mapping: [], remembered: false,
    mode: EXISTING_MODE.UPDATE,

    existing: null, existingStatus: 'idle', // idle | loading | ready | capped | error
    catalogue: null, catalogueStatus: 'idle', // idle | loading | ready | error
    // Adding catalogue entries is administrator-only on the API (POST 403), so the
    // option is offered only to administrators — the server still decides.
    canAddCatalogue: getUserData()?.userRole === 'Administrator',
    addToCatalogue: true,
    built: null, reviewFilter: 'all', reviewPage: 0,

    run: null, runFilter: 'all', runPage: 0
  };

  const root = el(importShellHTML());
  viewEl.replaceChildren(root);
  const stepsEl = root.querySelector('[data-imp-steps]');
  const msgEl = root.querySelector('[data-imp-msg]');
  const bodyEl = root.querySelector('[data-imp-body]');
  const footEl = root.querySelector('[data-imp-footer]');

  const sheet = () => state.sheets[state.sheetIndex] || { rows: [] };
  const yard = () => state.yards.find((y) => String(y.Id) === String(state.yardId)) || null;

  function setMsg(text, kind) {
    msgEl.textContent = text || '';
    msgEl.hidden = !text;
    msgEl.className = `tm-form-msg${kind ? ` tm-form-msg-${kind}` : ''}`;
  }

  // -------------------------------------------------------------------------
  // Rendering
  // -------------------------------------------------------------------------

  function render() {
    if (disposed) return;
    stepsEl.innerHTML = stepsHTML(state.step);
    if (state.step === 'file') renderFile();
    else if (state.step === 'map') renderMap();
    else if (state.step === 'review') renderReview();
    else renderRun();
  }

  function renderFile() {
    bodyEl.innerHTML = fileStepHTML({
      yardsStatus: state.yardsStatus, yardsError: state.yardsError, yards: state.yards, yardId: state.yardId,
      fileName: state.fileName, reading: state.reading,
      sheets: state.sheets, sheetIndex: state.sheetIndex, headerRow: state.headerRow,
      rowCount: sheet().rows.length, truncated: !!sheet().truncated,
      template: { busy: state.templateBusy, disabled: state.yardsStatus === 'loading' }
    });
    renderPreview();
    renderFileFooter();
  }

  function renderPreview() {
    const slot = bodyEl.querySelector('[data-imp-preview]');
    if (slot) slot.innerHTML = state.sheets.length ? previewHTML(sheet().rows, state.headerRow) : '';
  }

  function renderFileFooter() {
    const hasData = sheet().rows.length > state.headerRow + 1;
    footEl.innerHTML = fileFooterHTML({ canNext: !!yard() && hasData && !state.reading });
  }

  // Up to three DISTINCT values from the column — "Super Single ×3" tells the
  // user less than three different values would.
  function samplesFor(col) {
    const seen = new Set();
    const rows = sheet().rows;
    for (let r = state.headerRow + 1; r < rows.length && seen.size < 3 && r < state.headerRow + 500; r++) {
      const v = (rows[r] || [])[col];
      if (v !== null && v !== undefined && String(v).trim() !== '') seen.add(v);
    }
    return [...seen];
  }

  function renderMap() {
    bodyEl.innerHTML = mapStepHTML({
      headers: state.headers,
      mapping: state.mapping,
      samples: state.headers.map((_, c) => samplesFor(c)),
      mode: state.mode,
      remembered: state.remembered
    });
    renderMapStatus();
  }

  // Everything on the map step that depends on the mapping, minus the selects.
  function renderMapStatus() {
    const problems = mappingProblems(state.mapping, state.headers);
    const slot = bodyEl.querySelector('[data-imp-map-problems]');
    if (slot) slot.innerHTML = mapProblemsHTML(problems);
    bodyEl.querySelectorAll('[data-imp-map-row]').forEach((tr) => {
      tr.classList.toggle('is-mapped', !!state.mapping[Number(tr.dataset.impMapRow)]);
    });
    footEl.innerHTML = mapFooterHTML({ canNext: problems.length === 0 });
  }

  function reviewRows() {
    const pred = REVIEW_FILTERS[state.reviewFilter] || REVIEW_FILTERS.all;
    return (state.built?.rows || []).filter(pred);
  }

  function renderReview() {
    const filtered = reviewRows();
    state.reviewPage = clampPage(state.reviewPage, filtered.length);
    const start = state.reviewPage * PAGE_SIZE;
    bodyEl.innerHTML = reviewStepHTML({
      summary: state.built.summary,
      pageRows: filtered.slice(start, start + PAGE_SIZE),
      filteredTotal: filtered.length,
      filter: state.reviewFilter,
      page: state.reviewPage,
      pages: pageCount(filtered.length),
      yardName: yard()?.name,
      mode: state.mode,
      existingStatus: state.existingStatus,
      catalogue: {
        status: state.catalogueStatus === 'ready' ? 'ready' : state.catalogueStatus === 'error' ? 'error' : 'loading',
        plan: state.built.catalogue?.plan || [],
        missingRows: state.built.catalogue?.missingRows || 0,
        inactiveRows: state.built.catalogue?.inactiveRows || 0,
        canAdd: state.canAddCatalogue,
        add: state.addToCatalogue
      }
    });
    footEl.innerHTML = reviewFooterHTML({
      importable: state.built.summary.importable,
      errors: state.built.summary.error,
      busy: state.existingStatus === 'loading' || state.catalogueStatus === 'loading'
    });
  }

  function runItems() {
    const run = state.run;
    const pred = RUN_FILTERS[state.runFilter] || RUN_FILTERS.all;
    return run.rows
      .map((row) => ({ row, res: run.results.get(row.rowNumber) }))
      .filter(({ row, res }) => pred(row, res));
  }

  function renderRun() {
    const run = state.run;
    const items = runItems();
    state.runPage = clampPage(state.runPage, items.length);
    const start = state.runPage * PAGE_SIZE;
    const counts = resultCounts(run.rows, run.results);
    bodyEl.innerHTML = runStepHTML({
      done: run.done, total: run.rows.length, counts,
      running: run.running, stopping: run.stopRequested, stopped: run.stopped, authLost: run.authLost,
      catalogueRun: run.catalogue,
      pageItems: items.slice(start, start + PAGE_SIZE),
      filteredTotal: items.length,
      filter: state.runFilter,
      page: state.runPage,
      pages: pageCount(items.length)
    });
    footEl.innerHTML = runFooterHTML({
      running: run.running, stopping: run.stopRequested,
      failed: counts.failed, notSent: counts.notSent, yardName: yard()?.name
    });
  }

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------

  async function loadYards() {
    state.yardsStatus = 'loading';
    if (state.step === 'file') renderFile();
    const res = await fetchYards().catch((e) => ({ success: false, message: e?.message }));
    if (disposed) return;
    if (!res.success) {
      state.yardsStatus = 'error';
      state.yardsError = res.message || 'Could not load yards.';
    } else {
      state.yards = res.data?.results || [];
      state.yardsStatus = 'ready';
      if (!yard()) state.yardId = state.yards.length === 1 ? state.yards[0].Id : '';
    }
    if (state.step === 'file') renderFile();
  }

  // Loaders are memoised while in flight: the template download and the Review
  // step can both ask for the same list without fetching it twice.
  let suppliersLoading = null;
  function loadSuppliers() {
    if (suppliersLoading) return suppliersLoading;
    state.suppliersStatus = 'loading';
    suppliersLoading = (async () => {
      const res = await fetchSuppliers({ sort: 'supplierName' }).catch(() => ({ success: false }));
      if (disposed) return;
      state.suppliers = res.success ? (res.data?.results || []) : [];
      state.suppliersStatus = res.success ? 'ready' : 'error';
      if (state.step === 'review') rebuild();
    })().finally(() => { suppliersLoading = null; });
    return suppliersLoading;
  }

  // The preview of "already on file": the whole account, not just the target yard,
  // because an ID that lives in another yard is still the same tyre (an update
  // would move it). The runner re-checks every ID before writing regardless.
  async function loadExisting() {
    state.existingStatus = 'loading';
    const res = await fetchAllTyres({}).catch(() => ({ success: false }));
    if (disposed) return;
    if (!res.success) {
      state.existing = new Map();
      state.existingStatus = 'error';
    } else {
      const m = new Map();
      for (const t of res.data?.results || []) {
        const k = tyreIdKey(t?.externalTyreId);
        if (k) m.set(k, t);
      }
      state.existing = m;
      state.existingStatus = res.data?.capped ? 'capped' : 'ready';
    }
    if (state.step === 'review') rebuild();
  }

  // The whole catalogue, inactive entries included: a tyre matching an inactive
  // entry must not cause a duplicate to be created.
  let catalogueLoading = null;
  function loadCatalogue() {
    if (catalogueLoading) return catalogueLoading;
    state.catalogueStatus = 'loading';
    catalogueLoading = (async () => {
      const res = await fetchTyreCatalogue({ sort: 'manufacturerName,modelName' }).catch(() => ({ success: false }));
      if (disposed) return;
      state.catalogue = res.success ? (res.data?.results || []) : null;
      state.catalogueStatus = res.success ? 'ready' : 'error';
      if (state.step === 'review') rebuild();
    })().finally(() => { catalogueLoading = null; });
    return catalogueLoading;
  }

  // -------------------------------------------------------------------------
  // Import template (an .xlsx laid out like the tyre form, with this customer's
  // yards, suppliers and catalogue as dropdowns — see tyreimport-template.js).
  // Built in the browser from data this screen loads anyway; nothing is written.
  // -------------------------------------------------------------------------

  function customerNameForTemplate() {
    const own = getUserData()?.parentCustomerId;
    const y = state.yards.find((x) => own != null && String(x.customerId) === String(own)) || state.yards[0];
    return y?.customerName || '';
  }

  async function downloadTemplate() {
    if (state.templateBusy) return;
    state.templateBusy = true;
    setMsg('');
    if (state.step === 'file') renderFile();
    try {
      // Fresh lists: a supplier or catalogue entry added since this screen opened
      // belongs in the template. Yards are current (the header refreshes them).
      await Promise.all([loadSuppliers(), loadCatalogue()]);
      if (disposed) return;

      const unavailable = [];
      if (state.yardsStatus !== 'ready') unavailable.push('yards');
      if (state.suppliersStatus !== 'ready') unavailable.push('suppliers');
      if (state.catalogueStatus !== 'ready') unavailable.push('tyre catalogue');

      const generatedOn = todayInputValue();
      const customerName = customerNameForTemplate();
      const book = buildTemplateWorkbook({
        yards: state.yardsStatus === 'ready' ? state.yards : [],
        suppliers: state.suppliersStatus === 'ready' ? state.suppliers : [],
        catalogue: state.catalogueStatus === 'ready' ? state.catalogue : [],
        customerName,
        generatedOn,
        unavailable
      });
      const JSZip = (await import('jszip')).default;
      const bytes = await writeXlsx(book, JSZip);
      if (disposed) return;
      downloadBlob(templateFileName({ customerName, generatedOn }), new Blob([bytes], { type: XLSX_MIME }));

      if (unavailable.length) {
        setMsg(`Template downloaded, but the ${unavailable.join(', ')} could not be loaded, so ${unavailable.length === 1 ? 'that column has' : 'those columns have'} no dropdown.`, 'warn');
      } else {
        setMsg('Template downloaded. Fill it in, save it, then choose it here.', 'ok');
      }
    } catch (e) {
      if (!disposed) setMsg(`The template could not be created: ${e?.message || 'unknown error'}.`, 'error');
    } finally {
      state.templateBusy = false;
      if (!disposed && state.step === 'file') renderFile();
    }
  }

  async function handleFile(file) {
    if (!file || state.reading) return;
    state.reading = true;
    setMsg('');
    renderFile();
    try {
      const wb = await readSpreadsheet(file);
      if (disposed) return;
      const idx = Math.max(0, wb.sheets.findIndex((s) => s.rows.some((r) => r && r.length)));
      state.fileName = file.name;
      state.sheets = wb.sheets;
      state.sheetIndex = idx;
      state.headerRow = detectHeaderRow(wb.sheets[idx].rows);
      state.built = null;
      if (!(sheet().rows.length > state.headerRow + 1)) {
        setMsg('This sheet has no data rows under the header row.', 'warn');
      }
    } catch (e) {
      if (!disposed) setMsg(e?.message || 'The file could not be read.', 'error');
    } finally {
      state.reading = false;
      if (!disposed && state.step === 'file') renderFile();
    }
  }

  // -------------------------------------------------------------------------
  // Step transitions
  // -------------------------------------------------------------------------

  function goMap() {
    const rows = sheet().rows;
    const headers = headersFor(rows, state.headerRow);
    const signature = headerSignature(headers);
    const sameLayout = state.headers.length && headerSignature(state.headers) === signature && state.mapping.length === headers.length;
    if (!sameLayout) {
      const remembered = loadImportMapping(signature, headers.length);
      state.mapping = remembered || autoMap(headers);
      state.remembered = !!remembered && remembered.some(Boolean);
    }
    state.headers = headers;
    state.step = 'map';
    setMsg('');
    render();
  }

  function rebuild() {
    state.built = buildImportRows({
      rows: sheet().rows,
      headerRow: state.headerRow,
      mapping: state.mapping,
      ctx: {
        yard: yard(),
        yards: state.yards,
        suppliers: state.suppliers,
        existing: state.existing || new Map(),
        mode: state.mode,
        today: todayInputValue(),
        // Cells the import template's catalogue formulas filled (not typed) — on an
        // update they never replace a value the tyre already has.
        suggested: sheet().suggested,
        catalogue: state.catalogueStatus === 'ready' ? state.catalogue : null,
        addToCatalogue: state.canAddCatalogue && state.addToCatalogue,
        catalogueHint: state.canAddCatalogue
          ? 'Tick “Add … to the tyre catalogue” above to add it.'
          : 'An administrator can add it under Setup → Tyre catalogue.'
      }
    });
    if (state.step === 'review') renderReview();
  }

  function goReview() {
    saveImportMapping(headerSignature(state.headers), state.mapping);
    state.step = 'review';
    state.reviewFilter = 'all';
    state.reviewPage = 0;
    setMsg('');
    if (state.suppliersStatus === 'error') {
      setMsg('Suppliers could not be loaded, so supplier names in the file can’t be matched. Other columns are unaffected.', 'warn');
    }
    if (!state.existing || state.existingStatus === 'error') loadExisting();
    if (!state.catalogue || state.catalogueStatus === 'error') loadCatalogue();
    rebuild();
    render();
  }

  async function startRun(rowsSubset) {
    const rows = rowsSubset || rowsToRun(state.built.rows);
    if (!rows.length) return;
    const prior = rowsSubset && state.run ? state.run.results : null;
    // Catalogue entries are added on the first run. A retry keeps that report —
    // unless the session had expired before the catalogue phase could finish, in
    // which case the retry (after signing in again) runs it. runCatalogue re-plans
    // against a fresh read, so entries that did land are not added twice.
    const addCatalogue = (!rowsSubset || !!state.run?.catalogue?.authLost) &&
      state.canAddCatalogue && state.addToCatalogue &&
      (state.built.catalogue?.plan?.length || 0) > 0;
    state.run = {
      rows: rowsSubset && state.run ? state.run.rows : rows,
      results: prior || new Map(),
      catalogue: addCatalogue ? { running: true } : (rowsSubset && state.run ? state.run.catalogue : null),
      done: 0, running: true, stopRequested: false, stopped: false, authLost: false
    };
    // A retry keeps the earlier results for rows that already landed.
    if (prior) rows.forEach((r) => prior.delete(r.rowNumber));
    state.run.done = state.run.rows.length - rows.length;
    const baseDone = state.run.done;
    state.step = 'run';
    state.runFilter = 'all';
    state.runPage = 0;
    setMsg('');
    render();

    if (addCatalogue) {
      const cat = await runCatalogue(rows, {
        fetchCatalogue: () => fetchTyreCatalogue({ sort: 'manufacturerName,modelName' }),
        createEntry: createTyreCatalogueEntry
      });
      if (disposed) return;
      state.run.catalogue = cat;
      // The catalogue changed; the next Review must re-read it.
      state.catalogue = null;
      state.catalogueStatus = 'idle';
      if (cat.authLost) {
        Object.assign(state.run, { running: false, authLost: true });
        renderRun();
        return;
      }
      renderRun();
    }

    const outcome = await runImport(rows, {
      mode: state.mode,
      fetchTyre,
      saveTyres,
      shouldStop: () => disposed || state.run.stopRequested,
      onProgress: ({ done, results }) => {
        if (disposed) return;
        results.forEach((v, k) => state.run.results.set(k, v));
        state.run.done = baseDone + done;
        renderRun();
      }
    });
    if (disposed) return;
    outcome.results.forEach((v, k) => state.run.results.set(k, v));
    Object.assign(state.run, { running: false, stopped: outcome.stopped, authLost: outcome.authLost });
    // The account's tyres changed; the next Review must not use the old sweep.
    state.existing = null;
    state.existingStatus = 'idle';
    renderRun();
  }

  function resetForNewFile() {
    Object.assign(state, {
      step: 'file', fileName: '', sheets: [], sheetIndex: 0, headerRow: 0,
      headers: [], mapping: [], remembered: false, built: null, run: null
    });
    setMsg('');
    render();
  }

  // -------------------------------------------------------------------------
  // Downloads
  // -------------------------------------------------------------------------

  // The rows that could not be imported, with every ORIGINAL column plus the
  // reasons — the customer fixes this file and imports it again.
  function downloadErrors() {
    const rows = sheet().rows;
    const width = state.headers.length;
    const head = ['Row', ...state.headers.map((h, i) => h || `Column ${i + 1}`), 'Problems'];
    const lines = [head.map(csvCell).join(',')];
    for (const r of state.built.rows.filter((x) => x.action === IMPORT_ACTION.ERROR)) {
      const cells = rows[r.rowNumber - 1] || [];
      const vals = Array.from({ length: width }, (_, i) => cells[i] ?? '');
      lines.push([r.rowNumber, ...vals, r.errors.join(' ')].map(csvCell).join(','));
    }
    downloadCsv(`tyre-import-errors-${stamp()}.csv`, lines.join('\r\n'));
  }

  function downloadResults() {
    const labels = { create: 'New', update: 'Update', skip: 'Skip' };
    const status = {
      [IMPORT_RESULT.CREATED]: 'Created', [IMPORT_RESULT.UPDATED]: 'Updated',
      [IMPORT_RESULT.SKIPPED]: 'Skipped', [IMPORT_RESULT.FAILED]: 'Failed'
    };
    const lines = [['Row', 'Tyre ID', 'Planned', 'Result', 'Details'].map(csvCell).join(',')];
    for (const row of state.run.rows) {
      const res = state.run.results.get(row.rowNumber);
      lines.push([
        row.rowNumber, row.id, labels[row.action] || row.action,
        res ? status[res.status] : 'Not sent',
        res?.message || [...row.warnings].join(' ')
      ].map(csvCell).join(','));
    }
    downloadCsv(`tyre-import-results-${stamp()}.csv`, lines.join('\r\n'));
  }

  // -------------------------------------------------------------------------
  // Events (delegated — slots are repainted, listeners live on the root)
  // -------------------------------------------------------------------------

  root.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-imp-file]')) { handleFile(t.files?.[0]); t.value = ''; return; }
    if (t.matches('[data-imp-yard]')) { state.yardId = t.value; renderFileFooter(); return; }
    if (t.matches('[data-imp-sheet]')) {
      state.sheetIndex = Number(t.value) || 0;
      state.headerRow = detectHeaderRow(sheet().rows);
      renderFile();
      return;
    }
    if (t.matches('[data-imp-header]')) {
      const n = Math.round(Number(t.value));
      const max = Math.max(1, sheet().rows.length);
      state.headerRow = Math.min(Math.max(1, Number.isFinite(n) ? n : 1), max) - 1;
      t.value = String(state.headerRow + 1);
      renderPreview();
      renderFileFooter();
      return;
    }
    if (t.matches('[data-imp-map]')) {
      state.mapping[Number(t.dataset.impMap)] = t.value;
      state.remembered = false;
      renderMapStatus();
      return;
    }
    if (t.matches('[data-imp-mode]')) { state.mode = t.value; return; }
    if (t.matches('[data-imp-catalogue-add]')) { state.addToCatalogue = t.checked; rebuild(); }
  });

  root.addEventListener('click', (e) => {
    const b = e.target.closest('button');
    if (!b || b.disabled) return;

    if (b.matches('[data-imp-cancel]')) { if (typeof ctx.onClose === 'function') ctx.onClose(); return; }
    if (b.matches('[data-imp-retry-yards]')) { loadYards(); return; }
    if (b.matches('[data-imp-template]')) { downloadTemplate(); return; }
    if (b.matches('[data-imp-back]')) {
      state.step = state.step === 'review' ? 'map' : 'file';
      setMsg('');
      render();
      return;
    }
    if (b.matches('[data-imp-next]')) {
      if (state.step === 'file') goMap();
      else if (state.step === 'map') goReview();
      return;
    }
    if (b.matches('[data-imp-filter]')) {
      if (state.step === 'review') { state.reviewFilter = b.dataset.impFilter; state.reviewPage = 0; renderReview(); }
      else { state.runFilter = b.dataset.impFilter; state.runPage = 0; renderRun(); }
      return;
    }
    if (b.matches('[data-imp-page]')) {
      if (state.step === 'review') { state.reviewPage = Number(b.dataset.impPage); renderReview(); }
      else { state.runPage = Number(b.dataset.impPage); renderRun(); }
      bodyEl.scrollIntoView({ block: 'start', behavior: 'smooth' });
      return;
    }
    if (b.matches('[data-imp-download-errors]')) { downloadErrors(); return; }
    if (b.matches('[data-imp-start]')) { startRun(); return; }
    if (b.matches('[data-imp-stop]')) { state.run.stopRequested = true; renderRun(); return; }
    if (b.matches('[data-imp-download-results]')) { downloadResults(); return; }
    if (b.matches('[data-imp-retry]')) {
      const again = state.run.rows.filter((r) => {
        const res = state.run.results.get(r.rowNumber);
        return !res || res.status === IMPORT_RESULT.FAILED;
      });
      startRun(again);
      return;
    }
    if (b.matches('[data-imp-new]')) { resetForNewFile(); return; }
    if (b.matches('[data-imp-open-register]')) {
      if (typeof ctx.onOpenRegister === 'function') ctx.onOpenRegister(yard());
    }
  });

  // Drag-and-drop onto the file card.
  root.addEventListener('dragover', (e) => {
    const drop = e.target.closest('[data-imp-drop]');
    if (!drop) return;
    e.preventDefault();
    drop.classList.add('is-dragging');
  });
  root.addEventListener('dragleave', (e) => {
    const drop = e.target.closest('[data-imp-drop]');
    if (drop && !drop.contains(e.relatedTarget)) drop.classList.remove('is-dragging');
  });
  root.addEventListener('drop', (e) => {
    const drop = e.target.closest('[data-imp-drop]');
    if (!drop) return;
    e.preventDefault();
    drop.classList.remove('is-dragging');
    handleFile(e.dataTransfer?.files?.[0]);
  });

  render();
  loadYards();
  loadSuppliers();

  return () => { disposed = true; };
}
